import { useInView } from '@/hooks/useInView';
import { Link } from 'react-router-dom';
import {
  ChevronRight, CheckCircle, ShieldCheck, Handshake,
  Building2, Scale, Award, FileText, Download,
} from 'lucide-react';
import { useDynamicContent, usePublicSeo, usePublicDocuments } from '@/hooks/useDynamicContent';
import { resolveIcon } from '@/utils/iconMap';
import Seo from '@/seo/Seo';

const API = process.env.REACT_APP_BACKEND_URL;
const ICON_SIZE_SM = 22;
const GOV_IMG = 'https://images.unsplash.com/photo-1543372654-b45dd90eeee5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHx3YXNoaW5ndG9uJTIwZGMlMjBjYXBpdG9sJTIwYnVpbGRpbmclMjBkYXJrfGVufDB8fHx8MTc3NTk1MzQ5Mnww&ixlib=rb-4.1.0&q=85';

const DEFAULTS = {
  hero_title: 'Government',
  hero_title_gold: 'Contracting',
  hero_description: "We support federal agencies and work alongside prime contractors to deliver structured technology support, sustainment, and operational services. We understand the process, the compliance requirements, and what it takes to perform in these environments.",
  experience_heading: 'Federal Experience That Matters',
  experience_p1: "Score Integrated Solutions has direct experience supporting federal programs across defense, healthcare, and civilian sectors. We know these environments because we've worked inside them — not just as consultants, but as operational support partners.",
  experience_p2: 'Our work includes technology support and sustainment for the U.S. Army, healthcare IT support for the Defense Health Agency including MHS GENESIS, EHR support for the Department of Veterans Affairs, and operational coordination for the Defense Logistics Agency. We bring practical, hands-on experience to every engagement.',
  agencies: ['U.S. Army', 'DHA', 'VA', 'DLA', 'DoD'],
  capabilities: [
    { icon: 'Building2', title: 'Prime Contract Support', desc: 'We work alongside prime contractors, providing the technical and operational support their programs need. We integrate into existing team structures and deliver.' },
    { icon: 'Handshake', title: 'Teaming & Collaboration', desc: 'We partner with other firms when the work calls for it. Our focus is on getting the right support in place, not on being the largest team in the room.' },
    { icon: 'Scale', title: 'Small Business', desc: 'As a small business, we move quickly, communicate directly, and bring focused attention to the programs we support.' },
    { icon: 'Award', title: 'Demonstrated Performance', desc: 'Our work with the Army, DHA, VA, and DLA speaks for itself. We deliver on commitments and maintain strong working relationships.' },
  ],
  compliance_heading: 'Security & Regulatory Compliance',
  compliance_intro: 'We maintain rigorous compliance with all applicable federal regulations and security frameworks. Our processes are designed to meet the highest standards of government contracting.',
  compliance_items: ['Federal Acquisition Regulation (FAR)', 'NIST Cybersecurity Framework', 'FISMA Compliance', 'FedRAMP Authorization', 'HIPAA (Healthcare)', 'Section 508 Accessibility'],
  naics_codes: [
    { code: '541512', label: 'Computer Systems Design Services (Primary)' },
    { code: '541611', label: 'Management Consulting Services' },
    { code: '541511', label: 'Custom Computer Programming Services' },
    { code: '541519', label: 'Other IT Services' },
    { code: '518210', label: 'Cloud / Data Processing & Hosting' },
    { code: '541614', label: 'Logistics Consulting Services' },
    { code: '611430', label: 'Professional Training' },
    { code: '561210', label: 'Facilities Support Services' },
  ],
  certifications: ['CMMI Level 3', 'ISO 27001', '8(a) Certified', 'HUBZone', 'SAM Registered'],
  company_data: [
    { label: 'Legal Name', value: 'Score Integrated Solutions LLC' },
    { label: 'Business Type', value: 'Small Business' },
    { label: 'Location', value: 'Richmond, VA 23294' },
    { label: 'UEI', value: 'Pending (SAM.gov)' },
    { label: 'CAGE Code', value: 'Pending' },
    { label: 'Primary NAICS', value: '541512' },
  ],
  past_performance: [
    { agency: 'United States Army', scope: 'Technology & Operational Support' },
    { agency: 'Defense Health Agency', scope: 'Healthcare IT & MHS GENESIS Support' },
    { agency: 'Department of Veterans Affairs', scope: 'EHR & Systems Support' },
    { agency: 'Defense Logistics Agency', scope: 'Operational Support Services' },
  ],
  subcontracting_heading: 'Working With Prime Contractors',
  subcontracting_intro: 'Score Integrated Solutions works effectively as a subcontractor, bringing focused technology and operational support to larger federal programs. We integrate into existing team structures, follow established processes, and deliver the support that our prime partners need to meet their commitments.',
  subcontracting_items: [
    'Clean integration with existing program workflows',
    'Responsive staffing to meet changing requirements',
    'Consistent delivery and clear communication',
    'Technical depth across multiple support areas',
    'Full compliance with subcontracting requirements',
  ],
};

void [ShieldCheck, Handshake, Building2, Scale];

function Section({ children, className = '' }) {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className={`${className} transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
      {children}
    </section>
  );
}

export default function GovContractingPage() {
  const c = useDynamicContent('government', DEFAULTS);
  const seoOverrides = usePublicSeo('government');
  const documents = usePublicDocuments();

  return (
    <div className="min-h-screen bg-[#121212]">
      <Seo
        page="government"
        title={seoOverrides?.title}
        description={seoOverrides?.description}
        keywords={seoOverrides?.keywords}
        ogTitle={seoOverrides?.og_title}
        ogDescription={seoOverrides?.og_description}
      />
      <section data-testid="gov-hero" className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={GOV_IMG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#121212]/50 via-[#121212]/80 to-[#121212]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="inline-block text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold mb-4 border border-[#F2B705]/30 px-4 py-1.5">Federal Solutions</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white mb-6">
            {c.hero_title}<br />
            <span className="gold-gradient-text">{c.hero_title_gold}</span>
          </h1>
          <p className="text-base md:text-lg text-[#A3A3A3] max-w-2xl leading-relaxed">{c.hero_description}</p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#F2B705]/40 to-transparent" />
      </section>

      {/* Federal Experience */}
      <Section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-px bg-[#F2B705]" />
                <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">Our Record</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mt-3 mb-6">{c.experience_heading}</h2>
              <p className="text-sm text-[#A3A3A3] leading-relaxed mb-6">{c.experience_p1}</p>
              <p className="text-sm text-[#A3A3A3] leading-relaxed mb-8">{c.experience_p2}</p>
              <div className="flex flex-wrap gap-3 mb-8">
                {(c.agencies || []).map((agency) => (
                  <span key={agency} className="border border-[#F2B705]/30 text-[#F2B705] text-xs uppercase tracking-widest font-bold px-5 py-2">{agency}</span>
                ))}
              </div>
              <div className="border border-white/10 overflow-hidden relative">
                <img src="https://images.unsplash.com/photo-1708807472445-d33589e6b090?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxtaWxpdGFyeSUyMHRlY2hub2xvZ3klMjBkZWZlbnNlJTIwY3liZXJzZWN1cml0eSUyMG9wZXJhdGlvbnN8ZW58MHx8fHwxNzc1OTU3MTk1fDA&ixlib=rb-4.1.0&q=85" alt="Operations center" className="w-full h-52 object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121212]/60 to-transparent" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {(c.capabilities || []).map((cap, i) => {
                const Icon = resolveIcon(cap.icon, Building2);
                return (
                  <div key={cap.title || i} data-testid={`cap-card-${i}`} className="bg-[#161616] border border-white/10 p-6 card-hover gold-border-glow transition-all">
                    <Icon className="text-[#F2B705] mb-3" size={ICON_SIZE_SM} strokeWidth={1.5} />
                    <h3 className="text-sm font-semibold text-white mb-2">{cap.title}</h3>
                    <p className="text-xs text-[#A3A3A3] leading-relaxed">{cap.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Section>

      {/* Compliance & NAICS */}
      <Section className="py-24 md:py-32 bg-[#161616]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-px bg-[#F2B705]" />
                <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">Compliance</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mt-3 mb-6">{c.compliance_heading}</h2>
              <p className="text-sm text-[#A3A3A3] leading-relaxed mb-8">{c.compliance_intro}</p>
              <ul className="space-y-4">
                {(c.compliance_items || []).map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <ShieldCheck size={16} className="text-[#F2B705] shrink-0" />
                    <span className="text-sm text-white">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-px bg-[#F2B705]" />
                <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">NAICS Codes</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mt-3 mb-6">Contract Vehicles</h2>
              <div className="space-y-4">
                {(c.naics_codes || []).map((n) => (
                  <div key={n.code} className="flex items-start gap-4 p-4 border border-white/10 bg-[#121212] hover:border-[#F2B705]/30 transition-colors">
                    <span className="text-[#F2B705] font-bold text-sm shrink-0 font-mono">{n.code}</span>
                    <span className="text-sm text-[#A3A3A3]">{n.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Certifications */}
      <Section className="py-20 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-10">
            <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">Certifications</span>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            {(c.certifications || []).map((cert) => (
              <div key={cert} className="border border-[#F2B705]/30 px-8 py-4 text-xs tracking-widest uppercase font-bold text-[#F2B705] hover:bg-[#F2B705]/5 transition-colors animate-gold-pulse">{cert}</div>
            ))}
          </div>
        </div>
      </Section>

      {/* Company Data */}
      <Section className="py-20 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">Company Data</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mt-3">Score Integrated Solutions LLC</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(c.company_data || []).map((item) => (
              <div key={item.label} className="bg-[#161616] border border-white/10 p-6">
                <p className="text-xs uppercase tracking-widest text-[#525252] font-bold mb-1">{item.label}</p>
                <p className="text-sm font-semibold text-white">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Past Performance */}
      <Section className="py-20 bg-[#161616] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-12">
            <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">Past Performance</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mt-3">Representative Experience</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {(c.past_performance || []).map((pp) => (
              <div key={pp.agency} className="bg-[#121212] border border-white/10 p-8 card-hover gold-border-glow transition-all">
                <Award className="text-[#F2B705] mb-4" size={24} strokeWidth={1.5} />
                <h3 className="text-sm font-semibold text-white mb-2">{pp.agency}</h3>
                <p className="text-xs text-[#A3A3A3]">{pp.scope}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Capability Documents (only renders when admin has uploaded any) */}
      {documents.length > 0 && (
        <Section className="py-20 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="mb-10">
              <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">Resources</span>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mt-3">Capability Statements & Documents</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {documents.map((doc) => (
                <a
                  key={doc.id}
                  href={`${API}${doc.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`gov-doc-${doc.id}`}
                  className="bg-[#161616] border border-white/10 p-6 hover:border-[#F2B705]/30 transition-all flex items-start gap-4 group"
                >
                  <FileText className="text-[#F2B705] shrink-0 mt-1" size={20} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white group-hover:text-[#F2B705] transition-colors truncate">{doc.original_name}</p>
                    <p className="text-[10px] text-[#525252] uppercase tracking-widest mt-1 flex items-center gap-1.5">
                      <Download size={11} /> Download
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* Subcontracting */}
      <Section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">Partnership</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mt-3 mb-6">{c.subcontracting_heading}</h2>
            <p className="text-sm text-[#A3A3A3] leading-relaxed mb-6">{c.subcontracting_intro}</p>
            <ul className="space-y-3 mb-10">
              {(c.subcontracting_items || []).map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle size={16} className="text-[#F2B705] mt-0.5 shrink-0" />
                  <span className="text-sm text-[#A3A3A3]">{item}</span>
                </li>
              ))}
            </ul>
            <Link to="/contact" data-testid="gov-cta-btn" className="bg-[#F2B705] text-black font-bold text-sm uppercase tracking-wide px-10 py-4 hover:bg-[#D9A404] transition-colors inline-flex items-center gap-2">
              Partner With Us <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
